import { getLoggerManager } from '@/shared/logging'
import {
  GSPLAT_LOAD_TELEMETRY_SCHEMA_VERSION,
  type GSplatLoadByteAccounting,
  type GSplatLoadResourceSource,
  type GSplatLoadResourceType,
  type GSplatLoadStage,
  type GSplatLoadTelemetryEvent,
  type GSplatLoadTelemetryObserver,
  type GSplatLoadTelemetryResult,
} from '@/shared/types/gsplatTelemetry'

const log = getLoggerManager().getLogger('gsplat:telemetry')

const RESOURCE_SOURCES = new Set<GSplatLoadResourceSource>(['remote', 'local', 'cache', 'unknown'])

const NOOP_RESOURCE_ATTEMPT: GSplatResourceAttemptSpan = {
  markResponse: () => undefined,
  complete: () => undefined,
  fail: () => undefined,
}

export interface GSplatTelemetryResourceDescriptor {
  resourceType: GSplatLoadResourceType
  /** 只在引擎内用于生成 hash；不会出现在事件中。 */
  resourceIdentity: string
  /** 只在引擎内用于来源识别；不会出现在事件中。 */
  resourceUrl: string
  attempt?: number
}

export interface GSplatTelemetryStageDescriptor extends GSplatTelemetryResourceDescriptor {
  stage: GSplatLoadStage
}

export interface GSplatResourceAttemptSpan {
  markResponse(response: Response): void
  complete(bytes: number, accounting?: GSplatLoadByteAccounting): void
  fail(error: unknown, errorCode?: string): void
}

/** 当前进程的单调时钟。 */
export function getGSplatTelemetryNow(): number {
  return typeof globalThis.performance?.now === 'function'
    ? globalThis.performance.now()
    : Date.now()
}

/**
 * 从任意资源 identity 生成不可逆的短标识；不会保留 URL、query 或本地路径。
 */
export function createGSplatTelemetryResourceKey(
  resourceType: GSplatLoadResourceType,
  resourceIdentity: string
): string {
  const normalized = normalizeResourceIdentity(resourceIdentity)
  let hash = 0x811c9dc5
  for (let index = 0; index < normalized.length; index++) {
    hash ^= normalized.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return `${resourceType}:${(hash >>> 0).toString(16).padStart(8, '0')}`
}

/** 默认资源来源分类；cache 只有宿主显式确认时才会出现。 */
export function resolveGSplatLoadResourceSource(
  observer: GSplatLoadTelemetryObserver | undefined,
  url: string,
  response?: Response
): GSplatLoadResourceSource {
  try {
    const resolved = observer?.resolveResourceSource?.(url, response)
    if (resolved && RESOURCE_SOURCES.has(resolved)) {
      return resolved
    }
  } catch {
    // 来源辅助回调不能影响资源加载。
  }

  try {
    const base =
      typeof globalThis.location?.href === 'string' ? globalThis.location.href : undefined
    const parsed = base ? new URL(url, base) : new URL(url)
    const protocol = parsed.protocol.toLowerCase()
    const hostname = parsed.hostname.toLowerCase()

    if (
      protocol === 'file:' ||
      protocol === 'blob:' ||
      protocol === 'data:' ||
      protocol === 'content:' ||
      protocol === 'capacitor:' ||
      protocol === 'app:' ||
      protocol === 'uniapp:' ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0' ||
      hostname === '::1' ||
      hostname === '[::1]'
    ) {
      return 'local'
    }
    if (protocol === 'http:' || protocol === 'https:') {
      return 'remote'
    }
  } catch {
    return 'unknown'
  }

  return 'unknown'
}

/** 创建一个真实 fetch/read attempt 的低开销计时器。 */
export function beginGSplatResourceAttempt(
  observer: GSplatLoadTelemetryObserver | undefined,
  descriptor: GSplatTelemetryResourceDescriptor
): GSplatResourceAttemptSpan {
  if (!isTelemetryActive(observer)) return NOOP_RESOURCE_ATTEMPT

  const startedAtMs = getGSplatTelemetryNow()
  const attempt = Math.max(1, Math.floor(descriptor.attempt ?? 1))
  const resourceKey = createGSplatTelemetryResourceKey(
    descriptor.resourceType,
    descriptor.resourceIdentity
  )
  let source = resolveGSplatLoadResourceSource(observer, descriptor.resourceUrl)
  let headersAtMs: number | undefined
  let settled = false

  emit(observer, {
    schemaVersion: GSPLAT_LOAD_TELEMETRY_SCHEMA_VERSION,
    traceId: observer?.traceId ?? '',
    kind: 'resource-attempt-start',
    atMs: startedAtMs,
    resourceKey,
    resourceType: descriptor.resourceType,
    source,
    attempt,
  })

  const finish = (
    result: GSplatLoadTelemetryResult,
    bytes: number | undefined,
    byteAccounting: GSplatLoadByteAccounting,
    errorCode?: string
  ) => {
    if (settled) return
    settled = true
    const endedAtMs = getGSplatTelemetryNow()
    emit(observer, {
      schemaVersion: GSPLAT_LOAD_TELEMETRY_SCHEMA_VERSION,
      traceId: observer?.traceId ?? '',
      kind: 'resource-attempt-complete',
      atMs: endedAtMs,
      resourceKey,
      resourceType: descriptor.resourceType,
      source,
      attempt,
      startedAtMs,
      endedAtMs,
      durationMs: nonNegativeDuration(startedAtMs, endedAtMs),
      ...(headersAtMs === undefined
        ? {}
        : {
            ttfbMs: nonNegativeDuration(startedAtMs, headersAtMs),
            ...(result === 'success'
              ? { bodyReadMs: nonNegativeDuration(headersAtMs, endedAtMs) }
              : {}),
          }),
      ...(bytes === undefined ? {} : { bytes: Math.max(0, Math.floor(bytes)) }),
      byteAccounting,
      result,
      ...(errorCode ? { errorCode } : {}),
    })
  }

  return {
    markResponse(response: Response): void {
      if (settled || headersAtMs !== undefined) return
      headersAtMs = getGSplatTelemetryNow()
      source = resolveGSplatLoadResourceSource(observer, descriptor.resourceUrl, response)
    },
    complete(bytes: number, accounting = 'complete-response'): void {
      finish('success', bytes, accounting)
    },
    fail(error: unknown, errorCode?: string): void {
      finish(
        isAbortError(error) ? 'cancelled' : 'failure',
        undefined,
        'unavailable',
        errorCode ?? normalizeTelemetryErrorCode(error)
      )
    },
  }
}

/** 记录异步处理阶段，observer 失败不会影响原任务。 */
export async function measureGSplatLoadStage<T>(
  observer: GSplatLoadTelemetryObserver | undefined,
  descriptor: GSplatTelemetryStageDescriptor,
  task: () => Promise<T>
): Promise<T> {
  if (!isTelemetryActive(observer)) return task()

  const startedAtMs = getGSplatTelemetryNow()
  try {
    const value = await task()
    emitStage(observer, descriptor, startedAtMs, 'success')
    return value
  } catch (error) {
    emitStage(
      observer,
      descriptor,
      startedAtMs,
      isAbortError(error) ? 'cancelled' : 'failure',
      normalizeTelemetryErrorCode(error)
    )
    throw error
  }
}

/** 记录同步处理阶段，observer 失败不会影响原任务。 */
export function measureGSplatLoadStageSync<T>(
  observer: GSplatLoadTelemetryObserver | undefined,
  descriptor: GSplatTelemetryStageDescriptor,
  task: () => T
): T {
  if (!isTelemetryActive(observer)) return task()

  const startedAtMs = getGSplatTelemetryNow()
  try {
    const value = task()
    emitStage(observer, descriptor, startedAtMs, 'success')
    return value
  } catch (error) {
    emitStage(
      observer,
      descriptor,
      startedAtMs,
      isAbortError(error) ? 'cancelled' : 'failure',
      normalizeTelemetryErrorCode(error)
    )
    throw error
  }
}

/** 安全发送低频引擎里程碑。 */
export function emitGSplatLoadMilestone(
  observer: GSplatLoadTelemetryObserver | undefined,
  milestone: 'initial-load-batch-settled',
  durationMs: number,
  loadedResources: number,
  failedResources: number
): void {
  if (!isTelemetryActive(observer)) return

  const atMs = getGSplatTelemetryNow()
  emit(observer, {
    schemaVersion: GSPLAT_LOAD_TELEMETRY_SCHEMA_VERSION,
    traceId: observer?.traceId ?? '',
    kind: 'milestone',
    atMs,
    milestone,
    durationMs: Math.max(0, durationMs),
    loadedResources: Math.max(0, Math.floor(loadedResources)),
    failedResources: Math.max(0, Math.floor(failedResources)),
  })
}

function emitStage(
  observer: GSplatLoadTelemetryObserver | undefined,
  descriptor: GSplatTelemetryStageDescriptor,
  startedAtMs: number,
  result: GSplatLoadTelemetryResult,
  errorCode?: string
): void {
  const endedAtMs = getGSplatTelemetryNow()
  emit(observer, {
    schemaVersion: GSPLAT_LOAD_TELEMETRY_SCHEMA_VERSION,
    traceId: observer?.traceId ?? '',
    kind: 'stage-complete',
    atMs: endedAtMs,
    resourceKey: createGSplatTelemetryResourceKey(
      descriptor.resourceType,
      descriptor.resourceIdentity
    ),
    resourceType: descriptor.resourceType,
    source: resolveGSplatLoadResourceSource(observer, descriptor.resourceUrl),
    attempt: Math.max(1, Math.floor(descriptor.attempt ?? 1)),
    stage: descriptor.stage,
    startedAtMs,
    endedAtMs,
    durationMs: nonNegativeDuration(startedAtMs, endedAtMs),
    result,
    ...(errorCode ? { errorCode } : {}),
  })
}

function emit(
  observer: GSplatLoadTelemetryObserver | undefined,
  event: GSplatLoadTelemetryEvent
): void {
  if (!isTelemetryActive(observer)) return
  try {
    observer.onEvent(event)
  } catch {
    log.warn('[GSplatLoadTelemetry] observer callback failed')
  }
}

function isTelemetryActive(
  observer: GSplatLoadTelemetryObserver | undefined
): observer is GSplatLoadTelemetryObserver {
  return observer !== undefined && !observer.signal?.aborted && observer.traceId.length > 0
}

function normalizeResourceIdentity(identity: string): string {
  try {
    const base =
      typeof globalThis.location?.href === 'string' ? globalThis.location.href : undefined
    const parsed = base ? new URL(identity, base) : new URL(identity)
    parsed.username = ''
    parsed.password = ''
    parsed.search = ''
    parsed.hash = ''
    return `${parsed.protocol}//${parsed.host}${parsed.pathname}`
  } catch {
    return identity.replace(/[?#].*$/, '')
  }
}

function normalizeTelemetryErrorCode(error: unknown): string {
  if (isAbortError(error)) return 'aborted'
  if (error instanceof TypeError) return 'network-error'
  if (error instanceof SyntaxError) return 'parse-error'
  return 'resource-error'
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
  )
}

function nonNegativeDuration(startedAtMs: number, endedAtMs: number): number {
  return Math.max(0, endedAtMs - startedAtMs)
}
